import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AngularMaterialModule } from 'src/app/shared/module/angular-material.module';
import { ApplicationPipesModule } from 'src/app/shared/module/applicationPipesModule';
import { Select } from '@ngxs/store';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Dictionary } from 'lodash';
import { combineLatest, Observable, Subject, of, timer } from 'rxjs';
import { catchError, filter, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { Alert } from 'src/app/models/alert';
import { AlertTypes } from 'src/app/models/alertTypes';
import { GroupPartial } from 'src/app/models/groupPartial';
import { GroupValueKey } from 'src/app/models/constant/groupValueKey';
import { FeatureSettings, TenantFeatures, isFeatureEnabled } from 'src/app/models/featureSettings';
import { Vehicle } from 'src/app/models/vehicle';
import { VehicleState } from 'src/app/models/vehicleState';
import { AlertTypesState } from 'src/app/store/state/alert-types.state';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { SC_ASSETS_PATH, SpaRoutingService } from 'src/app/shared/services/spa.routing.service';
import { AppConfigurationService } from 'src/app/shared/services/app-configuration.service';
import { GoogleMapService } from 'src/app/services/google-map.service';
import { ServiceCenterStateService } from '../service-center-state.service';
import { AlertDetailService } from 'src/app/diagnostics/component/alert-detail/alert-detail.service';
import { AlertService } from 'src/app/diagnostics/component/alert/alert.service';
import { findShopGroup } from 'src/app/shared/handler/common-functions.util';
import { TelematicDriverService } from 'src/app/shared/services/telematic-driver.service';
import { VehicleService } from 'src/app/fleet/vehicle/service/vehicle.service';
import { AssetFaultService } from 'src/app/services/asset-fault.service';
import { AssetFault } from 'src/app/models/assetFault';
import { VehicleDetailService } from 'src/app/fleet/vehicle-detail/vehicle-detail.service';
import { FAULT_STATE_ACTIVE } from 'src/app/issues/issue-formatting';
import { DynamicDialogService } from 'src/app/dynamic-dialog/dynamic-dialog.service';
import { IssuesStateService } from 'src/app/issues/issues-state.service';
import { IssuesModule } from 'src/app/issues/issues.module';
import { ReportIssueFormComponent, ReportIssueResult } from 'src/app/issues/report-issue-form/report-issue-form.component';
import { AlertCaseComponent } from 'src/app/diagnostics/component/alert-case/alert-case.component';
import { CaseWorkflowContext, alertCaseWorkflowContext } from 'src/app/diagnostics/component/alert-case/case-workflow-context';
import { AuthorizationService } from 'src/app/services/authorization.service';
import { AuthService } from 'src/app/services/auth.service';
import { MaintainAlert, MaintainVehicle } from 'src/app/models/constant/right';
import { IssueDto } from 'src/app/issues/models/issue';
import { matchIssue } from './issue-match';
import { driftLabel, haversineMiles } from '../geo-distance';
import { AlertSummary } from '../models/service-center-asset-detail';
import { CaseRef, HISTORY_DAYS, HISTORY_LIMIT, HistoryRow, barHeightPx, isTruncated, sparklineBuckets, sparklineRead, toCaseRef, toHistoryRow, toRowCase } from './alert-history';
import { LatLng, MapPinKind, MapPlan, PIN_COLORS, PIN_LABEL_KEYS, buildMapPlan, fitMaxZoom, midpoint, stackedOn, usablePosition } from './alert-map';
import { alertIdFromLocation, hrefWithAlert, removeAlertParam } from './alert-link';
import { Lamp, changedLampKeys, lampSrc, lampStates } from './alert-lamps';
import { FaultSortKey, OTHER_FAULT_COLUMNS, SortDir, sortFaults } from './fault-sort';
import { FaultLine, FaultNow, buildFaultLines, faultCodeLabel } from './fault-lines';
import { AlertHistoryPanelComponent } from './alert-history-panel.component';
import { SensorGroup, SensorTrend, sensorGroups } from './alert-sensors';
import { SensorEvent } from 'src/app/models/sensorEvent';
import { LocationGridMapping } from 'src/app/models/locationGridMapping';
import { SensorLocationType } from 'src/app/models/constant/sensor';
import { buildLocationTypeMap } from '../sensor-display';
import { VehicleLayoutService } from 'src/app/fleet/vehicle-layout/vehicle-layout.service';
import { RecommendedActions, formatActions } from './recommended-actions';

export interface AlertDetailDialogData {
  alert: AlertSummary;
  vehicleName?: string;
  vin?: string;
  /** Enables the issue pane (issue lists are keyed by asset). Resolved from the vehicle when absent. */
  assetId?: string;
  /** The asset's current position; absent on surfaces without a card seed — drift row hides. */
  currentLatitude?: number;
  currentLongitude?: number;
  siblings?: AlertSiblings;
}

/** The asset's alerts in the opening surface's displayed order; they always feed Other open alerts. */
export interface AlertSiblings {
  alerts: AlertSummary[];
  startIndex: number;
  /** Also step through them with arrows and ←/→. */
  stepping: boolean;
}

export const ALERT_TRIAGE_DIALOG_CONFIG: MatDialogConfig = {
  panelClass: 'adlg-triage',
  width: '90vw',
  maxWidth: '1380px',
  height: '92vh',
  maxHeight: '92vh',
  autoFocus: false,
};

// The reconcile window after a 202 report; past it the pending note yields to the empty state.
const REPORT_SETTLE_MS = 10_000;

export const TRIAGE_MAP_ELEMENT_ID = 'adlgTriageMap';

// Movement only — never good/bad.
const TREND_GLYPHS: Record<Exclude<SensorTrend, null>, string> = { up: '↑', down: '↓', flat: '–' };
const TRIAGE_MAP_SINGLE_PIN_ZOOM = 13;
const TRIAGE_MAP_FIT_PADDING_PX = 40;
const TRIAGE_MAP_STACK_NUDGE_PX = 16;

// A manual report is not an Alert contribution, so matchIssue never finds it: the issue it
// minted (or merged into) is found by the report's own request id instead.
export function reportedIssue(issues: IssueDto[], report: ReportIssueResult | null): IssueDto | null {
  if (!report) return null;
  return issues.find(i => i.id === report.mergedIntoIssueId
    || (i.contributions ?? []).some(c => c.contributionId === report.requestId)) ?? null;
}

// Creating a case needs MaintainVehicle. Without it the case panel has nothing to offer on an
// alert that has no case, so it shows only once the read confirms a case to look at.
// caseAbsent is null until that read answers.
export function showsCasePanel(hasCaseContext: boolean, canCreateCase: boolean, caseAbsent: boolean | null): boolean {
  return hasCaseContext && (canCreateCase || caseAbsent === false);
}

@Component({
  selector: 'app-alert-detail-dialog',
  templateUrl: './alert-detail-dialog.component.html',
  styleUrl: './alert-detail-dialog.component.scss',
  standalone: true,
  imports: [CommonModule, AngularMaterialModule, TranslateModule, ApplicationPipesModule, IssuesModule, AlertCaseComponent, ReportIssueFormComponent, AlertHistoryPanelComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertDetailDialogComponent implements OnInit, OnDestroy {
  current: AlertSummary;
  siblingIndex = -1;

  loading = false;
  error = false;
  actions: RecommendedActions = formatActions(null);
  ruleSummary: string | null = null;
  ruleTags: string[] = [];
  triggeredFaults: FaultLine[] = [];
  otherFaults: FaultLine[] = [];
  visibleOtherFaults: FaultLine[] = [];
  otherFaultsOpen = true;
  readonly otherFaultsPageSize = 5;
  readonly otherFaultColumns = OTHER_FAULT_COLUMNS;
  otherSort: FaultSortKey = 'time';
  otherDir: SortDir = 'desc';
  otherPage = 0;

  paneIssue: IssueDto | null = null;
  issues: IssueDto[] = [];
  issuesLoading = false;
  issuesLoaded = false;
  reporting = false;
  pendingReport: ReportIssueResult | null = null;
  alertGroups: GroupPartial[] = [];
  alertCaseContext: CaseWorkflowContext | null = null;
  // The active-case read, warmed here so the panel can be gated on it and then paints
  // without a second round trip.
  alertCase$: Observable<any> | null = null;
  caseAbsent: boolean | null = null;
  openCase: CaseRef | null = null;
  shopGroup: GroupPartial | null = null;
  assignedLocation: string | null = null;
  driftDistance: string | null = null;

  readonly mapElementId = TRIAGE_MAP_ELEMENT_ID;
  mapPlan: MapPlan | null = null;
  mapsFailed = false;
  private mapsReady = false;
  private mapId: string | undefined;
  private mapEl: HTMLElement | null = null;
  private map: google.maps.Map | null = null;
  private pins: google.maps.marker.AdvancedMarkerElement[] = [];
  private driftLine: google.maps.Polyline | null = null;
  private renderedPlan: string | null = null;

  // Angular re-runs this setter whenever any embedded view in the template is added or removed.
  @ViewChild('triageMap') set triageMapRef(ref: ElementRef<HTMLElement> | undefined) {
    const el = ref?.nativeElement ?? null;
    if (el === this.mapEl) return;
    this.mapEl = el;
    this.renderMap();
  }

  vehicle: Vehicle | null = null;
  lamps: Lamp[] = lampStates(null);
  currentLamps: Lamp[] | null = null;
  changedLamps = new Set<string>();
  lampTab: 'alert' | 'current' = 'alert';
  siblingLamps: Record<string, Lamp[]> = {};
  sensorGroups: SensorGroup[] = [];
  sensorsOpen = false;
  private sensorLocationTypes: Map<string, SensorLocationType> | null = null;
  private currentSensors: SensorEvent[] | null = null;
  // Where the truck is now, for hosts that don't carry it (the alerts grid); the Service
  // Center passes its own from the asset it already has.
  private currentStatePosition: LatLng | null = null;
  private currentFaults: AssetFault[] | null = null;
  private currentStateVin: string | null = null;
  driverDict: Dictionary<string> | null = null;
  driverLabel: string | null = null;
  linkCopied = false;

  readonly historyDays = HISTORY_DAYS;
  readonly historyLimit = HISTORY_LIMIT;
  history: HistoryRow[] = [];
  historyLoaded = false;
  historyTruncated = false;
  buckets: number[] = [];
  bucketMax = 0;
  sparkRead: { count: number; days: number } | null = null;
  private historyKey: string | null = null;

  fullAlert: Alert | null = null;
  private loadedIssuesAssetId: string | null = null;
  private reportedIssueId: string | null = null;
  // Ignores responses from a superseded load after Next/Previous swaps the alert.
  private loadToken = 0;
  // Only once the URL has named this dialog's alert does its disappearance mean Back.
  private linked = false;
  private linkedAlertId = '';

  get alertLoaded(): boolean {
    return !!this.fullAlert;
  }

  @Select(AlertTypesState.getAlertTypes) getAlertTypes$: Observable<AlertTypes>;
  @SelectSnapshot(TenantSessionState.getTenantFeatures) private tenantFeatures: FeatureSettings;
  @SelectSnapshot(TenantSessionState.getLocationGridMapping) private locationGridMapping: LocationGridMapping;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AlertDetailDialogData,
    private dialogRef: MatDialogRef<AlertDetailDialogComponent, void>,
    private alertDetailService: AlertDetailService,
    private alertService: AlertService,
    private issuesState: IssuesStateService,
    private telematicDrivers: TelematicDriverService,
    private vehicleService: VehicleService,
    private vehicleLayouts: VehicleLayoutService,
    private assetFaults: AssetFaultService,
    private vehicleStates: VehicleDetailService,
    private dialogs: DynamicDialogService,
    private spaRouting: SpaRoutingService,
    private router: Router,
    private googleMaps: GoogleMapService,
    private appConfig: AppConfigurationService,
    private scState: ServiceCenterStateService,
    private authorization: AuthorizationService,
    private auth: AuthService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  get showCase(): boolean {
    return isFeatureEnabled(this.tenantFeatures, TenantFeatures.WorkOrderCreation);
  }

  get issuesEnabled(): boolean {
    return isFeatureEnabled(this.tenantFeatures, TenantFeatures.Issues)
      && isFeatureEnabled(this.tenantFeatures, TenantFeatures.IssuesUI);
  }

  get caseFlowEnabled(): boolean {
    return this.showCase && !this.issuesEnabled;
  }

  get canReport(): boolean {
    return this.issuesEnabled && this.authorization.hasRight(this.auth.user?.profile, MaintainAlert);
  }

  get showCasePanel(): boolean {
    return showsCasePanel(
      !!this.alertCaseContext && !!this.fullAlert,
      this.authorization.hasRight(this.auth.user?.profile, MaintainVehicle),
      this.caseAbsent);
  }

  get assetId(): string | undefined {
    return this.data.assetId ?? this.vehicle?.id;
  }

  ngOnInit(): void {
    this.current = this.data.alert;
    const siblings = this.siblingAlerts;
    if (siblings.length > 1) {
      const start = this.data.siblings.startIndex;
      if (start >= 0 && start < siblings.length) {
        this.siblingIndex = start;
        this.current = siblings[start];
      }
    }

    combineLatest([this.googleMaps.addScriptToHeader(), this.appConfig.settings$.pipe(take(1))])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: ([, settings]) => {
          this.mapId = settings.googleMapsAPIMapId;
          this.mapsReady = true;
          this.renderMap();
        },
        error: () => {
          this.mapsFailed = true;
          this.cdr.markForCheck();
        },
      });

    this.loadIssues();
    this.loadAlert();
    this.writeAlertParam(false);
    this.router.events.pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroy$)).subscribe(() => {
      if (alertIdFromLocation() === this.linkedAlertId) this.linked = true;
      else if (this.linked) this.dialogRef.close();
    });
  }

  // Opening earns a history entry; stepping replaces it so one Back closes the dialog.
  private writeAlertParam(replaceUrl: boolean): void {
    const alertId = this.current?.latestAlertId;
    if (!alertId) return;
    this.linkedAlertId = alertId;
    if (alertIdFromLocation() === alertId) return;
    void this.router.navigateByUrl(hrefWithAlert(window.location.href, alertId), { replaceUrl });
  }

  private loadIssues(): void {
    const assetId = this.assetId;
    if (!this.issuesEnabled || !assetId || assetId === this.loadedIssuesAssetId) return;
    this.loadedIssuesAssetId = assetId;
    this.issuesState.load(assetId);
    combineLatest([this.issuesState.issuesFor(assetId), this.issuesState.loadingFor(assetId)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([list, loading]) => {
        this.issues = list ?? [];
        this.issuesLoading = loading;
        if (!loading) this.issuesLoaded = true;
        this.updateIssuePane();
        this.cdr.markForCheck();
      });
  }

  private get siblingAlerts(): AlertSummary[] {
    return this.data.siblings?.alerts ?? [];
  }

  get siblingCount(): number {
    return this.siblingAlerts.length;
  }

  get hasSiblings(): boolean {
    return this.siblingIndex >= 0 && this.siblingCount > 1;
  }

  get showStepper(): boolean {
    return this.hasSiblings && this.data.siblings.stepping;
  }

  get canPrevious(): boolean {
    return this.showStepper && this.siblingIndex > 0;
  }

  get canNext(): boolean {
    return this.showStepper && this.siblingIndex < this.siblingCount - 1;
  }

  step(delta: number): void {
    if (!this.showStepper) return;
    this.stepTo(this.siblingIndex + delta);
  }

  stepTo(index: number): void {
    if (!this.hasSiblings || index < 0 || index >= this.siblingCount || index === this.siblingIndex) return;
    this.siblingIndex = index;
    this.current = this.siblingAlerts[index];
    this.loadAlert();
    this.writeAlertParam(true);
  }

  // A sibling steps in place; anything else replaces the dialog's alert and leaves the sibling list.
  show(alert: AlertSummary): void {
    if (!alert.latestAlertId || alert.latestAlertId === this.current?.latestAlertId) return;
    const siblingIndex = this.siblingAlerts.findIndex(s => s.latestAlertId === alert.latestAlertId);
    if (this.hasSiblings && siblingIndex >= 0) {
      this.stepTo(siblingIndex);
      return;
    }
    this.siblingIndex = -1;
    this.current = { ...this.current, ...alert };
    this.loadAlert();
    this.writeAlertParam(true);
    this.cdr.markForCheck();
  }

  trackOtherOpen = (_: number, o: { alert: AlertSummary }): string => o.alert.latestAlertId;

  get otherOpenAlerts(): { alert: AlertSummary; index: number }[] {
    if (!this.hasSiblings) return [];
    const vin = this.vin;
    return this.siblingAlerts
      .map((alert, index) => ({ alert, index }))
      .filter(o => o.index !== this.siblingIndex && (!o.alert.vin || !vin || o.alert.vin === vin));
  }

  barHeight(count: number): number {
    return barHeightPx(count, this.bucketMax);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') this.step(1);
    else if (event.key === 'ArrowLeft') this.step(-1);
  }

  private loadAlert(): void {
    const alertId = this.current?.latestAlertId;
    if (!alertId) return;

    const token = ++this.loadToken;
    this.loading = true;
    this.error = false;
    this.actions = formatActions(null);
    this.ruleSummary = null;
    this.ruleTags = [];
    this.triggeredFaults = [];
    this.otherFaults = [];
    this.visibleOtherFaults = [];
    this.otherFaultsOpen = true;
    this.otherSort = 'time';
    this.otherDir = 'desc';
    this.otherPage = 0;
    this.fullAlert = null;
    this.alertGroups = [];
    this.alertCaseContext = null;
    this.alertCase$ = null;
    this.caseAbsent = null;
    this.openCase = null;
    this.reporting = false;
    this.pendingReport = null;
    this.reportedIssueId = null;
    this.lamps = lampStates(null);
    this.refreshChangedLamps();
    this.lampTab = 'alert';
    this.sensorGroups = [];
    this.driverDict = null;
    this.driverLabel = null;
    this.applyLocation();
    this.updateIssuePane();
    if (this.vin !== this.vehicle?.vin) {
      this.vehicle = null;
      this.sensorLocationTypes = null;
    }
    this.loadHistory();

    combineLatest([
      this.alertDetailService.getData(alertId),
      this.getAlertTypes$.pipe(filter(t => !!t?.faultcodes?.length)),
    ]).pipe(
      take(1),
      takeUntil(this.destroy$),
    ).subscribe({
      next: ([alert, alertTypes]) => {
        if (token !== this.loadToken) return;
        this.fullAlert = alert;
        this.alertGroups = alert.groups ?? [];
        this.openCase = toRowCase(alert.cases?.[0]);
        this.alertCaseContext = this.caseFlowEnabled ? alertCaseWorkflowContext(this.alertDetailService, alert) : null;
        if (this.alertCaseContext) this.warmCase(this.alertCaseContext, token);
        this.actions = formatActions(alert.alertRule?.message);
        this.ruleSummary = alert.alertRule?.faultSummary || null;
        this.ruleTags = alert.alertRule?.tags ?? [];
        const lines = buildFaultLines(alert, alertTypes);
        this.triggeredFaults = lines.filter(l => l.triggeredAlert);
        this.otherFaults = lines.filter(l => !l.triggeredAlert);
        this.applyCurrentFaults();
        this.siblingLamps[alert.id] = lampStates(alert.vehicleState?.dashboard);
        this.loadSiblingLamps();
        this.applyLocation();
        this.applyVehicleState(alert.vehicleState, token);
        this.loadVehicle(alert.vehicleState?.vin, token);
        this.loadCurrentState(alert.vehicleState?.vin, token);
        this.sensorGroups = this.buildSensorGroups();
        this.loadHistory();
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        if (token !== this.loadToken) return;
        this.error = true;
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  // A failed read leaves caseAbsent null: the panel stays up for a user who may create a
  // case (it re-reads on its own), and stays hidden for one who may not.
  private warmCase(context: CaseWorkflowContext, token: number): void {
    this.alertCase$ = context.getActiveCase()
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.alertCase$.pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        if (token !== this.loadToken) return;
        this.caseAbsent = !(response && response.length > 0 && response[0]);
        if (!this.caseAbsent) this.openCase = toCaseRef(response[0].id, response[0].orderNumber);
        this.cdr.markForCheck();
      },
      error: () => { },
    });
  }

  private loadHistory(): void {
    const ruleId = this.current?.alertRuleId;
    const vin = this.vin;
    if (!ruleId || !vin) return;
    const key = `${ruleId}|${vin}`;
    if (key === this.historyKey) return;
    this.historyKey = key;
    this.history = [];
    this.historyLoaded = false;
    this.historyTruncated = false;
    this.buckets = [];
    this.bucketMax = 0;
    this.sparkRead = null;

    const createdFrom = new Date(Date.now() - HISTORY_DAYS * 86_400_000).toISOString();
    this.alertService.getAlertHistory(ruleId, vin, createdFrom, HISTORY_LIMIT).pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: page => {
        if (key !== this.historyKey) return;
        const rows = (page?.documents ?? []).map(toHistoryRow);
        this.history = rows;
        this.historyTruncated = isTruncated(rows, HISTORY_LIMIT);
        this.buckets = sparklineBuckets(rows);
        this.bucketMax = Math.max(0, ...this.buckets);
        this.sparkRead = sparklineRead(this.buckets);
        this.historyLoaded = true;
        this.cdr.markForCheck();
      },
      error: () => {
        if (key === this.historyKey) this.historyKey = null;
      },
    });
  }

  private applyVehicleState(state: VehicleState | undefined, token: number): void {
    this.lamps = lampStates(state?.dashboard);
    this.refreshChangedLamps();
    if (!state?.driverStatusList?.length) return;
    this.telematicDrivers.getDriversByVehicleState(state).pipe(takeUntil(this.destroy$)).subscribe(dict => {
      if (token !== this.loadToken) return;
      this.driverDict = dict;
      this.driverLabel = this.telematicDrivers.driverToString(dict, state.driverStatusList)[0] ?? null;
      this.cdr.markForCheck();
    });
  }

  private loadVehicle(vin: string | undefined, token: number): void {
    if (!vin || this.vehicle?.vin === vin) return;
    this.vehicleService.getVehicleByVin(vin).pipe(takeUntil(this.destroy$)).subscribe(vehicle => {
      if (token !== this.loadToken || !vehicle) return;
      this.vehicle = vehicle;
      this.sensorGroups = this.buildSensorGroups();
      this.loadIssues();
      this.cdr.markForCheck();
      this.loadSensorLayout(vehicle, token);
    });
  }

  // The part-type chip and the tire-first sort need the vehicle's layout — the
  // sensor event carries only the grid key.
  private loadSensorLayout(vehicle: Vehicle, token: number): void {
    of(vehicle).pipe(
      switchMap(v => this.vehicleLayouts.resolveLayoutForVehicle(v)),
      catchError(() => of(null)),
      takeUntil(this.destroy$),
    ).subscribe(layout => {
      if (token !== this.loadToken) return;
      this.sensorLocationTypes = buildLocationTypeMap(layout);
      this.sensorGroups = this.buildSensorGroups();
      this.cdr.markForCheck();
    });
  }

  trendGlyph(trend: SensorTrend): string {
    return trend ? TREND_GLYPHS[trend] : '';
  }

  private buildSensorGroups(): SensorGroup[] {
    return sensorGroups(this.fullAlert?.vehicleState?.sensors, this.currentSensors, {
      assetType: this.vehicle?.bodyClass,
      mapping: this.locationGridMapping?.mapping,
      locationTypes: this.sensorLocationTypes,
      translate: key => this.translate.instant(key),
    });
  }

  ngOnDestroy(): void {
    this.clearMapOverlays();
    this.destroy$.next();
    this.destroy$.complete();
    // A footer button that navigates away has its own destination; writing here would cancel it.
    if (!this.linkedAlertId || this.router.getCurrentNavigation() || alertIdFromLocation() !== this.linkedAlertId) return;
    removeAlertParam(this.router);
  }

  pinLabelKey(kind: MapPinKind): string {
    return PIN_LABEL_KEYS[kind];
  }

  private renderMap(): void {
    if (!this.mapsReady || !this.mapEl || !this.mapPlan) return;
    const plan = JSON.stringify(this.mapPlan);
    if (this.map?.getDiv() === this.mapEl && plan === this.renderedPlan) return;
    this.renderedPlan = plan;
    if (!this.map || this.map.getDiv() !== this.mapEl) {
      this.map = new google.maps.Map(this.mapEl, {
        mapId: this.mapId,
        center: this.mapPlan.pins[0].position,
        zoom: TRIAGE_MAP_SINGLE_PIN_ZOOM,
        disableDefaultUI: true,
        zoomControl: true,
        clickableIcons: false,
        gestureHandling: 'cooperative',
      });
    }
    this.clearMapOverlays();

    const bounds = new google.maps.LatLngBounds();
    this.mapPlan.pins.forEach((pin, index) => {
      const glyph = new google.maps.marker.PinElement({ background: PIN_COLORS[pin.kind], borderColor: '#fff', glyphColor: '#fff', scale: 0.9 });
      glyph.element.classList.add('adlg-pin', `adlg-pin-${pin.kind}`);
      // Same spot as an earlier pin: no zoom separates them, so shift the graphic aside.
      if (stackedOn(pin, this.mapPlan!.pins.slice(0, index))) {
        glyph.element.style.transform = `translateX(${TRIAGE_MAP_STACK_NUDGE_PX}px)`;
      }
      this.pins.push(new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position: pin.position,
        content: glyph.element,
        title: this.translate.instant(PIN_LABEL_KEYS[pin.kind]),
      }));
      bounds.extend(pin.position);
    });

    const drift = this.mapPlan.drift;
    if (drift) {
      this.driftLine = new google.maps.Polyline({
        map: this.map,
        path: [drift.from, drift.to],
        strokeOpacity: 0,
        clickable: false,
        icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: PIN_COLORS.fired, scale: 2.5 }, offset: '0', repeat: '12px' }],
      });
      const label = document.createElement('div');
      label.className = 'adlg-drift-label';
      label.textContent = drift.label;
      label.style.cssText = 'background:#fff;border:1px solid rgba(20,30,45,.2);border-radius:3px;padding:1px 6px;font:500 11px Roboto,sans-serif;color:#212529;white-space:nowrap';
      this.pins.push(new google.maps.marker.AdvancedMarkerElement({ map: this.map, position: midpoint(drift.from, drift.to), content: label }));
    }

    if (this.mapPlan.pins.length > 1) {
      this.map.fitBounds(bounds, TRIAGE_MAP_FIT_PADDING_PX);
      const maxZoom = fitMaxZoom(this.mapPlan.pins);
      google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
        if ((this.map?.getZoom() ?? 0) > maxZoom) this.map?.setZoom(maxZoom);
      });
    } else {
      this.map.setCenter(this.mapPlan.pins[0].position);
      this.map.setZoom(TRIAGE_MAP_SINGLE_PIN_ZOOM);
    }
  }

  private clearMapOverlays(): void {
    for (const pin of this.pins) pin.map = null;
    this.pins = [];
    this.driftLine?.setMap(null);
    this.driftLine = null;
  }

  openInServiceCenter(): void {
    const assetId = this.assetId;
    if (!assetId) return;
    this.dialogRef.close();
    const onThisAsset = this.router.url.split('?')[0].endsWith(`/${SC_ASSETS_PATH}/${assetId}`);
    if (onThisAsset) this.scState.requestFocusSelectedAsset();
    else this.spaRouting.navigateToServiceCenterAssetsAsset(assetId);
  }

  get severityClass(): string {
    return 'sev-' + (this.current?.severity ?? '').toLowerCase();
  }

  get vehicleName(): string | undefined {
    return this.current?.vehicleName ?? this.data.vehicleName ?? this.vehicle?.name;
  }

  get vin(): string | undefined {
    return this.current?.vin ?? this.data.vin ?? this.fullAlert?.vehicleState?.vin;
  }

  get makeModel(): string | null {
    const v = this.vehicle;
    if (!v) return null;
    const name = [v.make, v.model].filter(Boolean).join(' ');
    return [name, v.modelYear].filter(Boolean).join(' · ') || null;
  }

  get engine(): string | null {
    const b = this.vehicle?.buildConfiguration;
    return [b?.engineManufacturer, b?.engineModel].filter(Boolean).join(' ') || null;
  }

  get shownLamps(): Lamp[] {
    return this.lampTab === 'current' && this.currentLamps ? this.currentLamps : this.lamps;
  }

  private refreshChangedLamps(): void {
    this.changedLamps = changedLampKeys(this.lamps, this.currentLamps);
  }

  lampSrc(lamp: Lamp): string {
    return lampSrc(this.appConfig.settings?.contentDeliveryNetworkUrl ?? '', lamp);
  }

  codeLabel(fault: FaultLine): string {
    return faultCodeLabel(fault, (key, params) => this.translate.instant(key, params));
  }

  private loadCurrentState(vin: string | undefined, token: number): void {
    if (!vin || vin === this.currentStateVin) return;
    this.currentStateVin = vin;
    this.currentLamps = null;
    this.refreshChangedLamps();
    this.currentSensors = null;
    this.currentStatePosition = null;
    this.currentFaults = null;
    this.vehicleStates.getVehicleStateByVin(vin).pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (token !== this.loadToken) return;
      this.currentLamps = lampStates(state?.dashboard);
      this.refreshChangedLamps();
      this.currentSensors = state?.sensors ?? [];
      this.currentStatePosition = usablePosition(state?.position?.latitude, state?.position?.longitude);
      this.sensorGroups = this.buildSensorGroups();
      this.applyLocation();
      this.cdr.markForCheck();
    });
    this.assetFaults.getAssetFaultState(vin).pipe(takeUntil(this.destroy$)).subscribe(faults => {
      if (token !== this.loadToken) return;
      this.currentFaults = faults ?? [];
      this.applyCurrentFaults();
      this.cdr.markForCheck();
    });
  }

  private applyCurrentFaults(): void {
    const now = (line: FaultLine): FaultNow => {
      if (!this.currentFaults) return null;
      const match = this.currentFaults.find(f => f.protocol === line.protocol && f.faultCode === line.faultCode
        && f.failureMode === line.failureCode && f.controller === line.controller);
      return match?.faultState === FAULT_STATE_ACTIVE ? 'active' : 'cleared';
    };
    this.triggeredFaults = this.triggeredFaults.map(l => ({ ...l, now: now(l) }));
    this.otherFaults = this.otherFaults.map(l => ({ ...l, now: now(l) }));
    this.refreshOtherFaults();
  }

  // A field, not a getter: under OnPush a getter minting new rows per pass breaks hover and click.
  private refreshOtherFaults(): void {
    const start = this.otherPage * this.otherFaultsPageSize;
    this.visibleOtherFaults = sortFaults(this.otherFaults, this.otherSort, this.otherDir).slice(start, start + this.otherFaultsPageSize);
  }

  sortOtherFaults(key: FaultSortKey): void {
    if (this.otherSort === key) this.otherDir = this.otherDir === 'asc' ? 'desc' : 'asc';
    else {
      this.otherSort = key;
      this.otherDir = key === 'time' ? 'desc' : 'asc';
    }
    this.otherPage = 0;
    this.refreshOtherFaults();
  }

  onOtherFaultsPage(event: PageEvent): void {
    this.otherPage = event.pageIndex;
    this.refreshOtherFaults();
  }

  trackFault = (_: number, fault: FaultLine): string => `${fault.protocol}|${fault.faultCode}|${fault.failureCode}|${fault.controller}`;

  // ponytail: one GET per sibling; a lamps field on AlertSummaryDto would make this free.
  private loadSiblingLamps(): void {
    for (const s of this.siblingAlerts) {
      const id = s.latestAlertId;
      if (!id || id in this.siblingLamps) continue;
      this.alertDetailService.getData(id).pipe(takeUntil(this.destroy$)).subscribe(alert => {
        this.siblingLamps[id] = lampStates(alert?.vehicleState?.dashboard);
        this.cdr.markForCheck();
      });
    }
  }

  private updateIssuePane(): void {
    const reported = reportedIssue(this.issues, this.pendingReport);
    if (reported) {
      this.reportedIssueId = reported.id;
      this.pendingReport = null;
    }
    this.paneIssue = matchIssue(this.issues, this.current?.latestAlertId, this.current?.alertRuleId)
      ?? this.issues.find(i => i.id === this.reportedIssueId)
      ?? null;
  }

  onReported(result: ReportIssueResult): void {
    const assetId = this.assetId;
    this.reporting = false;
    this.pendingReport = result;
    if (assetId) this.issuesState.reconcileCreate(assetId, result.requestId);
    timer(REPORT_SETTLE_MS).pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.pendingReport !== result) return;
      this.pendingReport = null;
      this.cdr.markForCheck();
    });
  }

  refreshIssues(): void {
    const assetId = this.assetId;
    if (assetId) this.issuesState.load(assetId, true);
  }

  private applyLocation(): void {
    const groups = this.fullAlert?.groups ?? [];
    this.shopGroup = this.fullAlert ? findShopGroup(groups) : null;
    this.assignedLocation = groups.find(g => g.values && GroupValueKey.TelematicGroup in g.values)?.name ?? null;
    this.driftDistance = this.computeDrift();
    this.mapPlan = buildMapPlan(this.firedPosition(), this.currentPosition(), this.driftDistance);
    this.renderMap();
  }

  private firedPosition(): LatLng | null {
    const p = this.fullAlert?.vehicleState?.position;
    return usablePosition(p?.latitude, p?.longitude);
  }

  private currentPosition(): LatLng | null {
    return usablePosition(this.data.currentLatitude, this.data.currentLongitude) ?? this.currentStatePosition;
  }

  private computeDrift(): string | null {
    const fired = this.firedPosition();
    const current = this.currentPosition();
    if (!fired || !current) return null;
    return driftLabel(haversineMiles(fired.lat, fired.lng, current.lat, current.lng));
  }

  showDriverDetails(): void {
    const state = this.fullAlert?.vehicleState;
    if (!state || !this.driverDict) return;
    this.dialogs.openDriverDialog(state, this.driverDict);
  }

  viewVehicle(): void {
    const vin = this.vin;
    if (!vin) return;
    this.dialogRef.close();
    this.spaRouting.navigateToVehicle(vin);
  }

  copyLink(): void {
    navigator.clipboard?.writeText(location.href).then(() => {
      this.linkCopied = true;
      this.cdr.markForCheck();
      setTimeout(() => { this.linkCopied = false; this.cdr.markForCheck(); }, 1500);
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
